<template>
  <div :class="colors.background" class="flex items-center justify-center px-4 sm:px-6 lg:px-8 h-full">
    <main class="max-w-2xl w-full" role="main" aria-labelledby="error-title">
      <HLSpace vertical :size="24" align="center" class="text-center">
        <!-- Error Icon with Animation -->
        <div 
          :class="[colors.iconBackground, colors.iconBorder]"
          class="mx-auto h-24 w-24 rounded-full flex items-center justify-center mb-4 shadow-lg animate-bounce-slow"
          role="img"
          :aria-label="t('errorPages.notFound.ariaLabel.icon')"
        >
          <Icon 
            name="bug"
            :size="48"
            :class="`${colors.icon} transform hover:scale-110 transition-transform`"
          />
        </div>

        <!-- Error Message -->
        <Heading 
          level="h2"
          :class="`${colors.errorCode} mb-3`"
          class="text-6xl font-bold"
          :responsive="false"
          :aria-label="t('errorPages.notFound.ariaLabel.errorCode')"
        >
          {{ t('errorPages.notFound.code') }}
        </Heading>
        <Heading 
          level="h4"
          id="error-title"
          color="default"
          :class="`${colors.title} mb-3 text-2xl`"
          :responsive="false"
        >
          {{ t('errorPages.notFound.title') }}
        </Heading>
        <Text 
          as="p"
          variant="body"
          color="secondary"
          :class="`${colors.description} mb-6 max-w-lg mx-auto leading-relaxed`"
        >
          {{ t('errorPages.notFound.message') }}
        </Text>

        <!-- Primary Actions -->
        <HLSpace 
          :size="16"
          wrap
          justify="center"
          role="navigation"
          :aria-label="t('errorPages.notFound.ariaLabel.primaryActions')"
        >
          <!-- Go to Dashboard Button -->
          <HLButton
            href="/dashboard"
            color="blue"
            variant="primary"
            size="lg"
            :aria-label="t('errorPages.notFound.goToDashboard')"
          >
            <template #iconLeft>
              <Icon name="home" size="md" />
            </template>
            {{ t('errorPages.notFound.goToDashboard') }}
          </HLButton>

          <!-- Go Back Button -->
          <HLButton
            @click="goBack"
            color="gray"
            variant="secondary"
            size="lg"
            :aria-label="t('errorPages.notFound.goBack')"
          >
            <template #iconLeft>
              <Icon name="arrowLeft" size="md" />
            </template>
            {{ t('errorPages.notFound.goBack') }}
          </HLButton>
        </HLSpace>

        <!-- Help Links Section -->
        <QuickLinks
          :title="t('errorPages.notFound.suggestion')"
          :links="quickLinks"
          :dividerClass="`mt-8 pt-5 ${colors.divider}`"
          :titleColor="colors.helpText"
          :linkColor="colors.linkText"
          :ariaLabel="t('errorPages.notFound.ariaLabel.quickLinks')"
        />
      </HLSpace>
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { HLButton, HLSpace } from '@platform-ui/highrise'
import Heading from '@/components/ui/Heading.vue'
import Text from '@/components/ui/Text.vue'
import QuickLinks from '@/components/ui/QuickLinks.vue'
import Icon from '@/components/ui/Icon.vue'
import { t } from '@/i18n/en-us'
import { ERROR_PAGE_COLORS, BUTTON_COLORS } from '@/constants/uiColors'

const router = useRouter()

// Colors from constants
const colors = ERROR_PAGE_COLORS
const buttonColors = BUTTON_COLORS

// Quick navigation links - only valid routes
const quickLinks = computed(() => [
  {
    to: '/dashboard',
    label: t('errorPages.notFound.links.dashboard'),
    icon: 'dashboard'
  },
  {
    to: '/bugs',
    label: t('errorPages.notFound.links.bugs'),
    icon: 'bug'
  },
  {
    to: '/reports',
    label: t('errorPages.notFound.links.reports'),
    icon: 'report'
  },
  {
    to: '/profile',
    label: t('errorPages.notFound.links.profile'),
    icon: 'user'
  }
])

/**
 * Navigate back to previous page or dashboard
 * Intelligently handles navigation based on browser history
 * @returns {void}
 */
const goBack = () => {
  if (window.history.length > 1) {
    router.go(-1)
  } else {
    router.push('/dashboard')
  }
}
</script>

<style scoped>
/* Slow bounce animation for icon */
@keyframes bounce-slow {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-bounce-slow {
  animation: bounce-slow 3s ease-in-out infinite;
}
</style>
